/**
 * Regex pattern matching `<plan>…</plan>` tags in agent stop messages.
 *
 * The backend wraps plan content in these tags so the UI can distinguish
 * a plan from an ordinary final message and render the plan-approval flow
 * only when appropriate.
 */
const PLAN_TAG_PATTERN = /<plan>([\s\S]*?)<\/plan>/

/**
 * Returns `true` when the content contains a `<plan>…</plan>` block.
 */
export function hasPlanTags(content: string): boolean {
  return PLAN_TAG_PATTERN.test(content)
}

/**
 * Strips `<plan>` and `</plan>` tag markers from a string, keeping the
 * content between them intact. Useful for rendering plan content in the
 * session logs where the tags themselves shouldn't be visible.
 */
export function stripPlanTags(content: string): string {
  return content.replace(/<\/?plan>/g, '')
}

/**
 * Splits a stop-message string into the plan portion (inside `<plan>` tags)
 * and the remaining prose (everything outside those tags).
 *
 * If no `<plan>` tags are found, `planContent` is `null` and
 * `remainingContent` equals the original (trimmed) input.
 */
export function extractPlanContent(content: string): {planContent: string | null; remainingContent: string} {
  const match = content.match(PLAN_TAG_PATTERN)

  if (!match) {
    return {planContent: null, remainingContent: content.trim()}
  }

  // Strip any stray plan tags from both outputs (e.g. duplicate </plan> from model output)
  const planContent = stripPlanTags(match[1] ?? '').trim()
  const remainingContent = stripPlanTags(content.replace(PLAN_TAG_PATTERN, '')).trim()

  return {planContent, remainingContent}
}
